<template>
  <v-row wrap>
    <v-col cols="12">
      <v-card flat border>
        <v-card-text class="px-0 py-0">
          <div class="table-scroll">
            <table class="permissions-matrix">
              <thead>
                <tr>
                  <th class="custom_header_table custom-bg permission-col">{{ $t('permission', 2) }}</th>
                  <th class="custom_header_table custom-bg role-col" v-for="role in rolesList" :key="role.id">
                    <div class="role-header">
                      <router-link v-if="auth.check('edit role')" class="custom-a" :to="{ name: 'RolesEdit', params: { id: role.id }}">
                        {{ role.name }}
                      </router-link>
                      <span v-else>{{ role.name }}</span>
                      <div class="role-actions">
                        <v-btn v-if="auth.check('edit role')" variant="text" :to="{ name: 'RolesEdit', params: { id: role.id }}" icon size="small" flat>
                          <v-icon size="18" color="grey">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn v-if="auth.check('delete role')" variant="text" v-on:click="deleteRole(role.id)" icon size="small" flat>
                          <v-icon size="18" color="red">mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="group in groupedPermissions" :key="group.titleKey">
                  <tr class="group-row">
                    <th class="custom-bg-row permission-col group-title" :colspan="rolesList.length + 1">
                      {{ $t(group.titleKey) }}
                    </th>
                  </tr>
                  <tr v-for="permission in group.permissions" :key="permission.id">
                    <td class="permission-col">{{ $t(permissionTranslationKey(permission.name)) }}</td>
                    <td class="text-center" v-for="role in rolesList" :key="`${role.id}-${permission.id}`">
                      <v-icon v-if="roleHasPermission(role, permission.id)" color="green-darken-1">mdi-check</v-icon>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { computed, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex'
import { useAuth } from '@/utils/auth';
import Say from '@/utils/Say';
import sayErrorResponse from '@/mixins/sayErrorResponse';
import { groupPermissions, permissionTranslationKey } from '@/utils/permissionGroups';

const { t } = useI18n()
const store = useStore()
const auth = useAuth()

// Computed
const rolesList = computed(() => store.getters['rolesList'])
const permissionsList = computed(() => store.state.permissions.list)
const groupedPermissions = computed(() => groupPermissions(permissionsList.value))

const roleHasPermission = (role: any, permissionId: number): boolean => {
  return (role.permissions ?? []).includes(permissionId)
}

// Created Hook
onBeforeMount(() => {
  store.dispatch('permissions.fetch').catch(sayErrorResponse)
  store.dispatch('roles.fetch').catch(sayErrorResponse)

  // UI
  store.commit('app.setPageTitle', t('role', 2))
  store.commit('app.setBreadcrumbs', [
    { text: t('user', 2), routeName: 'Users' },
    { text: t('role', 2) }
  ])
})

const deleteRole = (id: any) => {
  const confirmAction = confirm(t('roleDeleteConfirm'))
  if (confirmAction) {
    store.dispatch('roles.delete', id).then(() => {
      Say('success', t('roleDeleteSuccess'))
    }).catch((err) => {
      sayErrorResponse(err)
    })
  }
}
</script>

<style scoped>
  .table-scroll {
    overflow-x: auto;
  }

  table.permissions-matrix {
    width: 100%;
    border-spacing: 0;
    background-color: #fff;
    font-size: 10.5px;
    border-collapse: collapse;
  }

  table.permissions-matrix .permission-col {
    background-color: #fff;
    white-space: nowrap;
  }

  table.permissions-matrix thead th {
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  table.permissions-matrix .role-col {
    text-align: center;
    min-width: 140px;
  }

  table.permissions-matrix .role-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  table.permissions-matrix tbody tr:not(.group-row) {
    transition-duration: 0.28s;
    transition-property: background;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  }

  table.permissions-matrix tbody tr:not(.group-row):nth-child(even) {
    background: #f5f5f5;
  }

  table.permissions-matrix tbody tr:not(.group-row):hover {
    background-color: rgba(0, 0, 0, 0.07);
  }

  table.permissions-matrix tbody tr:not(.group-row) .permission-col {
    background-color: inherit;
  }

  table.permissions-matrix td {
    padding: 6px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  table.permissions-matrix .group-title {
    padding: 8px 16px;
    font-weight: bold;
    text-align: left;
  }
</style>
